import React, { useState } from "react";
import { ChevronDown, X, SlidersHorizontal } from "lucide-react";

/**
 * FilterSidebar — AmbitionBox-style collapsible filter rail for /opportunities.
 *
 * Sections are pluggable — parent passes an array of section descriptors
 * (see PostingsPublic.jsx for the concrete config). Each section renders a
 * chevron-toggle header and a checkbox list. Applied filters are surfaced as
 * dismissable pills at the very top for quick clearing.
 *
 * Props:
 *   sections     — [{ key, label, options: [{ value, label, count? }] }, ...]
 *   values       — { [sectionKey]: string[] }   currently checked values
 *   onChange     — (sectionKey, nextValues[]) => void
 *   onClearAll   — () => void
 *   defaultOpen  — string[]   section keys open by default
 *   className    — passthrough for wrappers
 */
export default function FilterSidebar({
  sections = [],
  values = {},
  onChange,
  onClearAll,
  defaultOpen = [],
  className = "",
}) {
  const [open, setOpen] = useState(() => new Set(defaultOpen));

  const toggleSection = (key) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleValue = (sectionKey, value) => {
    const current = values[sectionKey] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange?.(sectionKey, next);
  };

  const appliedCount = sections.reduce(
    (acc, s) => acc + (values[s.key]?.length || 0),
    0,
  );

  const appliedPills = sections.flatMap((s) =>
    (values[s.key] || []).map((v) => {
      const opt = s.options.find((o) => o.value === v);
      return { section: s.key, sectionLabel: s.label, value: v, label: opt?.label || v };
    }),
  );

  return (
    <aside
      className={`bg-white border border-rule rounded-xl overflow-hidden ${className}`}
      data-testid="filter-sidebar"
      aria-label="Filters"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-rule">
        <div className="inline-flex items-center gap-2 font-semibold text-sm">
          <SlidersHorizontal size={15} className="text-signal" />
          Filters
          {appliedCount > 0 && (
            <span className="text-[11px] font-bold bg-signal text-white rounded-full px-1.5 min-w-[18px] text-center">
              {appliedCount}
            </span>
          )}
        </div>
        {appliedCount > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="text-xs font-semibold text-signal hover:underline"
            data-testid="filter-clear-all"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Applied pills */}
      {appliedPills.length > 0 && (
        <div className="px-3 pt-3 flex flex-wrap gap-1.5" data-testid="applied-filter-pills">
          {appliedPills.map((pill) => (
            <button
              key={`${pill.section}-${pill.value}`}
              type="button"
              onClick={() => toggleValue(pill.section, pill.value)}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-signal/10 text-signal text-[11px] font-semibold border border-signal/25 hover:bg-signal/20 transition"
              data-testid={`applied-pill-${pill.section}-${pill.value}`}
            >
              {pill.label}
              <X size={11} strokeWidth={2.5} />
            </button>
          ))}
        </div>
      )}

      {/* Sections */}
      <div className="divide-y divide-rule">
        {sections.map((s) => {
          const isOpen = open.has(s.key);
          const selected = values[s.key] || [];
          return (
            <div key={s.key} data-testid={`filter-section-${s.key}`}>
              <button
                type="button"
                onClick={() => toggleSection(s.key)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold hover:bg-slate-50/50 transition"
                aria-expanded={isOpen}
                data-testid={`filter-section-toggle-${s.key}`}
              >
                <span className="flex items-center gap-2">
                  {s.label}
                  {selected.length > 0 && (
                    <span className="text-[11px] font-bold text-signal">({selected.length})</span>
                  )}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isOpen && (
                <div className="px-4 pb-3 space-y-1.5 max-h-64 overflow-y-auto" data-testid={`filter-section-body-${s.key}`}>
                  {s.options.length === 0 && (
                    <p className="text-xs text-muted italic py-1">No options available</p>
                  )}
                  {s.options.map((opt) => {
                    const checked = selected.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:text-signal transition"
                        data-testid={`filter-opt-${s.key}-${opt.value}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleValue(s.key, opt.value)}
                          className="accent-signal w-4 h-4 rounded shrink-0"
                          aria-label={opt.label}
                        />
                        <span className={`flex-1 min-w-0 truncate ${checked ? "font-semibold text-signal" : "text-slate-700"}`}>
                          {opt.label}
                        </span>
                        {opt.count != null && (
                          <span className="text-[11px] text-muted">{opt.count}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
